import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { productionReadinessPRT001Api, type PRT001Status, type ProgramDashboard, type ProgramValidation } from '../../api/production-readiness-prt001';

type Tab = 'overview' | 'workstreams' | 'deliverables' | 'gates' | 'performance' | 'status';

export function ProductionReadinessPRT001Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<ProgramDashboard | null>(null);
  const [workstreams, setWorkstreams] = useState<{ key: string; name: string; category: string; progress_pct: number }[]>([]);
  const [deliverables, setDeliverables] = useState<{ key: string; name: string; type: string; status: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [targets, setTargets] = useState<{ key: string; surface: string; target: string; unit: string }[]>([]);
  const [status, setStatus] = useState<PRT001Status | null>(null);
  const [validation, setValidation] = useState<ProgramValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-emerald-200 bg-white dark:border-emerald-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-emerald-700 dark:text-emerald-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, ws, del, gt, perf, st] = await Promise.all([
        productionReadinessPRT001Api.dashboard(), productionReadinessPRT001Api.workstreams(),
        productionReadinessPRT001Api.deliverables(), productionReadinessPRT001Api.qualityGates(),
        productionReadinessPRT001Api.performance(), productionReadinessPRT001Api.status(),
      ]);
      setDashboard(dash); setWorkstreams(ws); setDeliverables(del); setGates(gt); setTargets(perf); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load program center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await productionReadinessPRT001Api.validate({
        zero_critical_bugs: true, zero_data_corruption: true, zero_tenant_leakage: true,
        zero_security_critical_findings: true, performance_budgets_achieved: true,
        scalability_targets_achieved: true, accessibility_passes: true,
        ci_cd_stable: true, observability_operational: true,
        backup_verified: true, recovery_verified: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Production Readiness Program passes all release gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'workstreams', label: 'Workstreams' },
    { id: 'deliverables', label: 'Deliverables' }, { id: 'gates', label: 'Quality Gates' },
    { id: 'performance', label: 'Performance' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-emerald-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>PRT-000001 · P0 · Release Blocker</p>
        <h1 className="text-2xl font-semibold">Production Readiness Program</h1>
        <p className={`text-sm ${muted}`}>Quality · Stability · Reliability · Scalability · Operational Excellence</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/marketplace-operating-brain-dev110" className={`${accent} hover:underline`}>DEV-000110</Link>
          <span className={muted}>→</span>
          <Link to="/production-readiness" className={`${accent} hover:underline`}>Live Readiness Center</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-emerald-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">No New Features — Quality & Operational Excellence Only</h2>
            <p className={`mt-2 text-sm ${muted}`}>Transform the Marketplace Intelligence Platform from completed engineering into a production-ready SaaS platform for controlled external beta.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            {[{ label: 'Objectives', value: dashboard.objectives }, { label: 'Workstreams', value: dashboard.workstreams },
              { label: 'Deliverables', value: dashboard.deliverables }, { label: 'Quality Gates', value: dashboard.quality_gates }].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>{m.label}</p><p className="text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white disabled:opacity-50">Validate Release Gates</button>
          {validation && <p className={`text-sm ${validation.valid ? 'text-emerald-600' : 'text-red-600'}`}>{validation.valid ? 'All gates passed — GO for pilot' : validation.gaps?.join(', ')}</p>}
        </div>
      )}

      {tab === 'workstreams' && workstreams.map((w) => (
        <div key={w.key} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{w.name}</p><span className={`text-sm ${accent}`}>{w.progress_pct}%</span></div>
          <p className={`text-xs ${muted}`}>{w.category}</p>
        </div>
      ))}

      {tab === 'deliverables' && deliverables.map((d) => (
        <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{d.name}</p>
          <p className={`text-xs ${muted}`}>{d.type} · {d.status}</p>
        </div>
      ))}

      {tab === 'gates' && gates.map((g) => (
        <div key={g.key} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{g.name}</p>
          {g.blocking && <p className={`text-xs ${muted}`}>Blocking</p>}
        </div>
      ))}

      {tab === 'performance' && targets.map((t) => (
        <div key={t.key} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{t.surface}</p>
          <p className={`text-sm ${accent}`}>{t.target} {t.unit}</p>
        </div>
      ))}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <pre className="overflow-auto text-xs">{JSON.stringify(status, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
